/**
 * FrozenView — заглушка для замороженных вкладок (Вариант A).
 * Показывает описание функции и три кнопки голосования.
 */
import type { VoteValue, ViewVoteCounts } from '@/hooks/useIntegrityVotes';

interface FrozenConfig {
  icon: string;
  title: string;
  description: string;
}

export const FROZEN_VIEW_CONFIG: Record<string, FrozenConfig> = {
  graph: {
    icon: '🔗',
    title: 'Граф нормативных связей',
    description:
      'Визуальный граф: раздел ПД → замечание → норма. Показывает, где кластеры проблем и что на что влияет.',
  },
  conflicts: {
    icon: '⚡',
    title: 'Конфликты',
    description:
      'Замечания, на которые проектировщик дал ответ, но куратор ещё не рассмотрел. Workflow принятия и отклонения ответов.',
  },
  cascade: {
    icon: '🌊',
    title: 'Зона влияния',
    description:
      'Какие разделы ПД потребуют изменений и какие замечания связаны при исправлении каждого открытого.',
  },
  waves: {
    icon: '↔️',
    title: 'Сравнение редакций',
    description:
      'Diff документов и замечаний между двумя редакциями: что добавилось, изменилось или удалилось.',
  },
  compliance: {
    icon: '✅',
    title: 'Соответствие нормам',
    description:
      'Автоматическая проверка по каталогу требований 87-ФЗ с указанием конкретных пунктов.',
  },
  audit: {
    icon: '📋',
    title: 'Журнал аудита',
    description:
      'Полная история всех действий по проекту — загрузки, замечания, ответы, закрытия — с фильтрами.',
  },
  routing: {
    icon: '🗺️',
    title: 'Маршрутизация замечаний',
    description:
      'Автоматическое и ручное назначение замечаний на специалистов по разделам и ролям.',
  },
  summary: {
    icon: '📝',
    title: 'Сводка для ГЭ',
    description:
      'Автогенерация таблицы ответов на замечания в формате, принятом в государственной экспертизе.',
  },
  aiview: {
    icon: '🤖',
    title: 'ИИ-анализ',
    description:
      'Кластеризация замечаний по темам, автоматическая приоритизация и поиск скрытых связей нейросетью.',
  },
};

interface Props {
  viewId: string;
  counts: ViewVoteCounts | undefined;
  onVote: (value: VoteValue) => void;
}

export function FrozenView({ viewId, counts, onVote }: Props) {
  const cfg = FROZEN_VIEW_CONFIG[viewId];
  if (!cfg) return null;

  const userVote = counts?.user_vote as VoteValue | undefined;
  const totalCritical = counts?.critical ?? 0;
  const totalNice = counts?.nice ?? 0;
  const totalAll = totalCritical + totalNice + (counts?.not_needed ?? 0);

  return (
    <div
      style={{
        maxWidth: 640,
        margin: '0 auto',
        padding: 40,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 24,
      }}
    >
      {/* Icon + badge */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 48, lineHeight: 1 }}>{cfg.icon}</div>
        <span
          style={{
            display: 'inline-block',
            padding: '3px 12px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            background: 'rgba(107,114,128,.1)',
            color: '#6B7280',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          В разработке
        </span>
      </div>

      {/* Title + description */}
      <div>
        <h3
          className="font-heading text-ink"
          style={{ fontSize: 22, fontWeight: 700, margin: '0 0 10px' }}
        >
          {cfg.title}
        </h3>
        <p
          className="text-muted"
          style={{ fontSize: 14, lineHeight: 1.65, margin: 0, maxWidth: 480 }}
        >
          {cfg.description}
        </p>
      </div>

      {/* Vote block */}
      <div
        style={{
          width: '100%',
          padding: '24px 28px',
          borderRadius: 16,
          border: '1px dashed #E5E7EB',
          background: '#F9FAFB',
        }}
      >
        {userVote ? (
          /* Уже проголосовал */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 28 }}>✓</div>
            <p className="text-ink" style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
              Вы ответили:{' '}
              <span style={{ color: userVote === 'critical' ? '#0A1628' : '#6B7280' }}>
                {userVote === 'critical'
                  ? 'Очень нужна'
                  : userVote === 'nice'
                  ? 'Когда-нибудь пригодится'
                  : 'Не нужна'}
              </span>
            </p>
            {totalAll > 0 && (
              <p className="text-muted" style={{ fontSize: 12, margin: 0 }}>
                Проголосовали: {totalAll} · Считают важной: {totalCritical + totalNice}
              </p>
            )}
            <button
              type="button"
              onClick={() => onVote(userVote)}
              style={{
                marginTop: 4,
                fontSize: 11,
                color: '#9CA3AF',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Изменить ответ
            </button>
          </div>
        ) : (
          /* Ещё не голосовал */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <p
              className="text-ink"
              style={{ fontSize: 14, fontWeight: 600, margin: 0 }}
            >
              Насколько вам нужна эта функция?
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <VoteButton
                label="✦ Очень нужна"
                primary
                onClick={() => onVote('critical')}
              />
              <VoteButton
                label="Когда-нибудь пригодится"
                onClick={() => onVote('nice')}
              />
              <VoteButton
                label="Не нужна"
                muted
                onClick={() => onVote('not_needed')}
              />
            </div>
            {totalAll > 0 && (
              <p className="text-muted" style={{ fontSize: 11, margin: 0 }}>
                Уже проголосовали: {totalAll}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VoteButton({
  label,
  primary,
  muted,
  onClick,
}: {
  label: string;
  primary?: boolean;
  muted?: boolean;
  onClick: () => void;
}) {
  const base: React.CSSProperties = {
    padding: '9px 20px',
    borderRadius: 24,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid',
    transition: 'opacity .15s',
  };
  const style: React.CSSProperties = primary
    ? { ...base, background: '#0A1628', color: '#FFFFFF', borderColor: '#0A1628' }
    : muted
    ? { ...base, background: 'transparent', color: '#9CA3AF', borderColor: '#E5E7EB' }
    : { ...base, background: 'transparent', color: '#374151', borderColor: '#D1D5DB' };

  return (
    <button type="button" style={style} onClick={onClick}>
      {label}
    </button>
  );
}
